import { supabase } from '../lib/supabase';
import { getVisitorId, getSessionId } from '../utils/tracking';

// With several open follow-ups per person, the "active" one is the next one due.
const pickActive = (reminders) =>
  (reminders || [])
    .filter(r => !r.is_completed)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))[0] || null;

export const leadService = {
  submitLead: async (slug, data) => {
    const { data: leadId, error } = await supabase.rpc('submit_card_lead', {
      p_slug: slug,
      p_full_name: data.fullName,
      p_email: data.email,
      p_phone: data.phone || null,
      p_company: data.company || null,
      p_job_title: data.jobTitle || null,
      p_message: data.message || null,
      p_consent: data.consent,
      p_source: data.source || 'public_card',
      p_qr_token: data.qrToken || null,
      p_visitor_id: getVisitorId(),
      p_session_id: getSessionId(),
      p_contact_photo_url: data.contactPhotoUrl || null
    });

    if (error) throw error;
    return leadId;
  },

  getLeads: async (userId) => {
    const { data, error } = await supabase
      .from('card_leads')
      .select(`
        *,
        cards(internal_name, public_slug),
        events(id, name),
        connection_tags(user_tags(id, name)),
        connection_reminders(*),
        connection_outcomes(*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return (data || []).map(lead => ({
      ...lead,
      tags: lead.connection_tags?.map(ct => ct.user_tags) || [],
      activeReminder: pickActive(lead.connection_reminders),
      outcomes: lead.connection_outcomes || []
    }));
  },

  getEventLeads: async (eventId, userId) => {
    const { data, error } = await supabase
      .from('card_leads')
      .select(`
        *,
        cards(internal_name, public_slug),
        connection_tags(user_tags(id, name)),
        connection_reminders(*),
        connection_outcomes(*)
      `)
      .eq('event_id', eventId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(lead => ({
      ...lead,
      tags: lead.connection_tags?.map(ct => ct.user_tags) || [],
      activeReminder: pickActive(lead.connection_reminders),
      outcomes: lead.connection_outcomes || []
    }));
  },

  updateLead: async (id, userId, updates) => {
    const { data, error } = await supabase
      .from('card_leads')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select(`
        *,
        cards(internal_name, public_slug),
        events(id, name),
        connection_tags(user_tags(id, name)),
        connection_reminders(*),
        connection_outcomes(*)
      `)
      .single();

    if (error) throw error;

    return {
      ...data,
      tags: data.connection_tags?.map(ct => ct.user_tags) || [],
      activeReminder: pickActive(data.connection_reminders),
      outcomes: data.connection_outcomes || []
    };
  },

  // Connection Outcomes
  addOutcome: async (connectionId, userId, outcomeType, note, date) => {
    const { data, error } = await supabase
      .from('connection_outcomes')
      .insert({
        connection_id: connectionId,
        user_id: userId,
        outcome_type: outcomeType,
        note: note,
        outcome_date: date
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  removeOutcome: async (outcomeId, userId) => {
    const { error } = await supabase
      .from('connection_outcomes')
      .delete()
      .eq('id', outcomeId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  getRecentLeads: async (userId, limit = 5) => {
    const { data, error } = await supabase
      .from('card_leads')
      .select('*, cards(internal_name)')
      .eq('user_id', userId)
      .eq('is_archived', false)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  getConnectionStats: async (userId) => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    const [totalRes, weekRes, monthRes] = await Promise.all([
      supabase.from('card_leads').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('is_archived', false),
      supabase.from('card_leads').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('is_archived', false).gte('created_at', startOfWeek.toISOString()),
      supabase.from('card_leads').select('id', { count: 'exact', head: true }).eq('user_id', userId).eq('is_archived', false).gte('created_at', startOfMonth.toISOString())
    ]);

    return {
      total: totalRes.count || 0,
      thisWeek: weekRes.count || 0,
      thisMonth: monthRes.count || 0
    };
  },

  addTag: async (connectionId, tagName) => {
    if (!tagName.trim()) return;
    const { error } = await supabase.rpc('add_connection_tag', {
      p_connection_id: connectionId,
      p_tag_name: tagName
    });
    if (error) throw error;
  },

  removeTag: async (connectionId, tagId) => {
    const { error } = await supabase.rpc('remove_connection_tag', {
      p_connection_id: connectionId,
      p_tag_id: tagId
    });
    if (error) throw error;
  },

  // Adds an independent follow-up. Existing open follow-ups are left untouched.
  saveReminder: async (connectionId, userId, dueDate, note = '') => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .insert({
        connection_id: connectionId,
        user_id: userId,
        due_date: dueDate.toISOString(),
        note: note
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  completeReminder: async (reminderId, userId) => {
    const { error } = await supabase
      .from('connection_reminders')
      .update({ is_completed: true, completed_at: new Date().toISOString() })
      .eq('id', reminderId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  deleteReminder: async (reminderId, userId) => {
    const { error } = await supabase
      .from('connection_reminders')
      .delete()
      .eq('id', reminderId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  getTimeline: async (connectionId, userId) => {
    const { data, error } = await supabase
      .from('connection_events')
      .select('*')
      .eq('connection_id', connectionId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  getActiveReminders: async (userId) => {
    const { data, error } = await supabase
      .from('connection_reminders')
      .select('*, card_leads(full_name, company_name, job_title, is_important, status)')
      .eq('user_id', userId)
      .eq('is_completed', false)
      .order('due_date', { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // ----------------------------------------------------------------------
  // Photos & Memories
  // ----------------------------------------------------------------------
  
  getMemories: async (connectionId, userId) => {
    const { data, error } = await supabase
      .from('connection_memories')
      .select('*')
      .eq('connection_id', connectionId)
      .eq('user_id', userId)
      .order('memory_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Generate signed URLs for private images
    const memories = await Promise.all(data.map(async (m) => {
      const { data: urlData } = await supabase.storage
        .from('connection_memories')
        .createSignedUrl(m.image_path, 7 * 24 * 60 * 60); // 7 days expiry
      return { ...m, signed_url: urlData?.signedUrl };
    }));

    return memories;
  },

  addMemory: async (connectionId, userId, memoryData, file) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
    const filePath = `${userId}/${connectionId}/${fileName}`;

    // Upload to private bucket
    const { error: uploadError } = await supabase.storage
      .from('connection_memories')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Save metadata
    const { data, error } = await supabase
      .from('connection_memories')
      .insert({
        connection_id: connectionId,
        user_id: userId,
        image_path: filePath,
        memory_date: memoryData.memory_date || null,
        event_name: memoryData.event_name || null,
        location: memoryData.location || null,
        caption: memoryData.caption || null,
        notes: memoryData.notes || null
      })
      .select()
      .single();

    if (error) throw error;

    // Return with immediately generated signed URL
    const { data: urlData } = await supabase.storage
      .from('connection_memories')
      .createSignedUrl(filePath, 7 * 24 * 60 * 60);

    return { ...data, signed_url: urlData?.signedUrl };
  },

  updateMemory: async (memoryId, userId, updates) => {
    const { data, error } = await supabase
      .from('connection_memories')
      .update(updates)
      .eq('id', memoryId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data; 
  },

  deleteMemory: async (memoryId, userId, imagePath) => {
    const { error } = await supabase
      .from('connection_memories')
      .delete()
      .eq('id', memoryId)
      .eq('user_id', userId);

    if (error) throw error;

    // Clean up storage
    await supabase.storage
      .from('connection_memories')
      .remove([imagePath]);
  },

  setMemoryAsProfilePhoto: async (memory, userId, connectionId) => {
    // Fetch blob from private signed URL
    const res = await fetch(memory.signed_url);
    const blob = await res.blob();

    const ext = memory.image_path.split('.').pop() || 'jpg';
    const fileName = `inbound/${Date.now()}-${Math.random().toString(36).substring(7)}_profile.${ext}`;

    // Upload to public contact_photos bucket
    const { error: uploadError } = await supabase.storage
      .from('contact_photos')
      .upload(fileName, blob);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from('contact_photos')
      .getPublicUrl(fileName);

    // Update lead record
    return await leadService.updateLead(connectionId, userId, { contact_photo_url: data.publicUrl });
  }
};